import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { getAccessToken } from "@/lib/auth-utils";

const API = import.meta.env.VITE_BACKEND_URL || "http://localhost:8001";

// ─── Types ───────────────────────────────────────────────────────────────────

interface VersionMeta {
    id: number;
    document_id: string;
    version_number: number;
    storage_url: string;
    edited_by: string;
    comment: string;
    is_frozen: boolean;
    created_at: string;
}

interface DiffHunk {
    op: "equal" | "insert" | "delete";
    text: string;
}

interface DiffResult {
    version_from: number;
    version_to: number;
    hunks: DiffHunk[];
    from_meta: { comment: string; created_at: string };
    to_meta: { comment: string; created_at: string };
}

// ─── Diff Viewer ─────────────────────────────────────────────────────────────

const DiffViewer: React.FC<{ diff: DiffResult }> = ({ diff }) => {
    if (!diff || !diff.from_meta || !diff.to_meta) return null;
    return (
        <div className="diff-viewer">
            <div className="diff-header">
                <span className="diff-from">
                    v{diff.version_from} — {diff.from_meta.comment}
                </span>
                <span className="diff-arrow">→</span>
                <span className="diff-to">
                    v{diff.version_to} — {diff.to_meta.comment}
                </span>
            </div>
            <pre className="diff-pre">
                {diff.hunks.map((hunk, i) => (
                    <span
                        key={i}
                        className={
                            hunk.op === "insert"
                                ? "diff-insert"
                                : hunk.op === "delete"
                                    ? "diff-delete"
                                    : "diff-equal"
                        }
                    >
                        {hunk.text}
                    </span>
                ))}
            </pre>
        </div>
    );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const DocumentHistory: React.FC = () => {
    const { documentId } = useParams<{ documentId: string }>();
    const navigate = useNavigate();

    const [versions, setVersions] = useState<VersionMeta[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Diff state
    const [selectMode, setSelectMode] = useState(false);
    const [selectedVersions, setSelectedVersions] = useState<number[]>([]);
    const [diff, setDiff] = useState<DiffResult | null>(null);
    const [diffLoading, setDiffLoading] = useState(false);

    // Restore state
    const [restoring, setRestoring] = useState<number | null>(null);

    // Delete state
    const [deleting, setDeleting] = useState<number | null>(null);

    // ── Fetch versions ──────────────────────────────────────────────────────────
    useEffect(() => {
        if (!documentId) return;
        setLoading(true);
        getAccessToken().then(token => {
            fetch(`${API}/api/v1/documents/${documentId}/versions`, {
                headers: {
                    "Authorization": `Bearer ${token || ""}`
                }
            })
                .then(async (r) => {
                    const contentType = r.headers.get("content-type") || "";
                    if (!r.ok || !contentType.includes("application/json")) {
                        const text = await r.text();
                        throw new Error(text.startsWith("%PDF") ? "Error: El servidor retornó un PDF en vez del historial JSON" : `Error ${r.status}: No se pudo cargar el historial de versiones`);
                    }
                    return r.json();
                })
                .then((d) => setVersions(d.versions || []))
                .catch((e) => setError(e.message))
                .finally(() => setLoading(false));
        });
    }, [documentId]);

    // ── Diff ────────────────────────────────────────────────────────────────────
    const fetchDiff = async () => {
        if (selectedVersions.length !== 2) return;
        const [a, b] = [...selectedVersions].sort((x, y) => x - y);
        setDiffLoading(true);
        try {
            const token = await getAccessToken();
            const r = await fetch(
                `${API}/api/v1/documents/${documentId}/versions/diff?from=${a}&to=${b}`,
                {
                    headers: {
                        "Authorization": `Bearer ${token || ""}`
                    }
                }
            );
            const data = await r.json();
            setDiff(data);
        } catch (e: any) {
            setError(e.message);
        } finally {
            setDiffLoading(false);
        }
    };

    const toggleVersionSelect = (v: number) => {
        setSelectedVersions((prev) =>
            prev.includes(v)
                ? prev.filter((x) => x !== v)
                : prev.length < 2
                    ? [...prev, v]
                    : [prev[1], v]
        );
    };

    // ── Restore ─────────────────────────────────────────────────────────────────
    const restoreVersion = async (versionNumber: number) => {
        if (!window.confirm(`¿Restaurar versión ${versionNumber}? Se creará una nueva versión.`))
            return;
        setRestoring(versionNumber);
        try {
            const token = await getAccessToken();
            await fetch(
                `${API}/api/v1/documents/${documentId}/versions/${versionNumber}/restore`,
                {
                    method: "POST",
                    headers: { 
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token || ""}`
                    },
                    body: JSON.stringify({ restored_by: "current-user" }),
                }
            );
            const r = await fetch(
                `${API}/api/v1/documents/${documentId}/versions`,
                {
                    headers: {
                        "Authorization": `Bearer ${token || ""}`
                    }
                }
            );
            const d = await r.json();
            setVersions(d.versions || []);
        } catch (e: any) {
            setError(e.message);
        } finally {
            setRestoring(null);
        }
    };

    // ── Delete ──────────────────────────────────────────────────────────────────
    const deleteVersion = async (versionNumber: number) => {
        if (!window.confirm(`¿Estás seguro de que deseas eliminar la versión ${versionNumber}? Esta acción es permanente.`))
            return;

        setDeleting(versionNumber);
        try {
            const token = await getAccessToken();
            const r = await fetch(
                `${API}/api/v1/documents/${documentId}/versions/${versionNumber}`,
                {
                    method: "DELETE",
                    headers: {
                        "Authorization": `Bearer ${token || ""}`
                    }
                }
            );
            if (r.ok) {
                setVersions(prev => prev.filter(v => v.version_number !== versionNumber));
            } else {
                const data = await r.json();
                setError(data.detail || "Error al eliminar la versión");
            }
        } catch (e: any) {
            setError(e.message);
        } finally {
            setDeleting(null);
        }
    };

    // ── Freeze ──────────────────────────────────────────────────────────────────
    const freezeVersion = async (versionNumber: number) => {
        if (!window.confirm(`¿Congelar versión ${versionNumber}? No podrá modificarse.`)) return;
        try {
            const token = await getAccessToken();
            await fetch(
                `${API}/api/v1/documents/${documentId}/versions/${versionNumber}/freeze`,
                { 
                    method: "POST",
                    headers: {
                        "Authorization": `Bearer ${token || ""}`
                    }
                }
            );
            setVersions((prev) =>
                prev.map((v) =>
                    v.version_number === versionNumber ? { ...v, is_frozen: true } : v
                )
            );
        } catch (e: any) {
            setError(e.message);
        }
    };

    // ── Render ──────────────────────────────────────────────────────────────────
    return (
        <div className="doc-history-page">
            {/* Header */}
            <div className="doc-history-header">
                <button className="btn-back" onClick={() => navigate("/dashboard/documents")}>
                    ← Volver
                </button>
                <h1>📋 Historial de versiones</h1>
                <div className="header-actions">
                    <button
                        className={`btn-diff-mode ${selectMode ? "active" : ""}`}
                        onClick={() => {
                            setSelectMode(!selectMode);
                            setSelectedVersions([]);
                            setDiff(null);
                        }}
                    >
                        {selectMode ? "Cancelar comparación" : "🔍 Comparar versiones"}
                    </button>
                    {selectMode && selectedVersions.length === 2 && (
                        <button className="btn-compare" onClick={fetchDiff}>
                            Ver diferencias
                        </button>
                    )}
                </div>
            </div>

            {error && <div className="error-banner">⚠️ {error}</div>}

            {/* Diff panel */}
            {diff && !diffLoading && (
                <div className="diff-panel">
                    <DiffViewer diff={diff} />
                    <button className="btn-close-diff" onClick={() => setDiff(null)}>
                        Cerrar
                    </button>
                </div>
            )}

            {/* Timeline */}
            {loading ? (
                <div className="loading-spinner">Cargando historial...</div>
            ) : versions.length === 0 ? (
                <div className="empty-state">Sin versiones registradas.</div>
            ) : (
                <div className="version-timeline">
                    {versions.map((v) => (
                        <div
                            key={v.id}
                            className={`version-card ${v.is_frozen ? "frozen" : ""} ${selectMode && selectedVersions.includes(v.version_number)
                                    ? "selected"
                                    : ""
                                }`}
                            onClick={() => selectMode && toggleVersionSelect(v.version_number)}
                        >
                            {/* Version badge + frozen badge */}
                            <div className="version-badge-row">
                                <span className="version-number">v{v.version_number}</span>
                                {v.is_frozen && (
                                    <span className="frozen-badge">🔒 Congelada</span>
                                )}
                                {v.version_number === versions[0]?.version_number && (
                                    <span className="latest-badge">ACTUAL</span>
                                )}
                            </div>

                            {/* Commit message */}
                            <p className="version-comment">{v.comment || "Sin comentario"}</p>

                            {/* Metadata row */}
                            <div className="version-meta">
                                <span className="version-date">
                                    🕐{" "}
                                    {format(new Date(v.created_at), "d MMM yyyy, HH:mm", {
                                        locale: es,
                                    })}
                                </span>
                            </div>

                            {/* Actions */}
                            {!selectMode && (
                                <div className="version-actions">
                                    {v.storage_url && (
                                        <a
                                            href={v.storage_url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="btn-action"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            ⬇ Descargar
                                        </a>
                                    )}
                                    {v.version_number !== versions[0]?.version_number && (
                                        <button
                                            className="btn-action"
                                            disabled={restoring === v.version_number}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                restoreVersion(v.version_number);
                                            }}
                                        >
                                            {restoring === v.version_number ? "..." : "↩ Restaurar"}
                                        </button>
                                    )}
                                    {!v.is_frozen && (
                                        <button
                                            className="btn-action btn-freeze"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                freezeVersion(v.version_number);
                                            }}
                                        >
                                            🔒 Congelar
                                        </button>
                                    )}
                                    <button
                                        className="btn-action btn-delete"
                                        disabled={deleting === v.version_number}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            deleteVersion(v.version_number);
                                        }}
                                    >
                                        {deleting === v.version_number ? "..." : "🗑️ Eliminar"}
                                    </button>
                                </div>
                            )}

                            {/* Select checkbox in compare mode */}
                            {selectMode && (
                                <input
                                    type="checkbox"
                                    className="version-checkbox"
                                    checked={selectedVersions.includes(v.version_number)}
                                    onChange={() => toggleVersionSelect(v.version_number)}
                                    onClick={(e) => e.stopPropagation()}
                                />
                            )}
                        </div>
                    ))}
                </div>
            )}

            <style>{`
        .doc-history-page {
          max-width: 900px;
          margin: 0 auto;
          padding: 2rem;
          font-family: 'Inter', sans-serif;
          color: #e2e8f0;
        }
        .doc-history-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 2rem;
          flex-wrap: wrap;
        }
        .doc-history-header h1 {
          flex: 1;
          font-size: 1.5rem;
          font-weight: 700;
          margin: 0;
        }
        .btn-back {
          background: transparent;
          border: 1px solid #4a5568;
          color: #a0aec0;
          padding: 0.4rem 0.8rem;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.85rem;
        }
        .header-actions { display: flex; gap: 0.75rem; }
        .btn-diff-mode, .btn-compare {
          padding: 0.5rem 1rem;
          border-radius: 8px;
          font-size: 0.85rem;
          font-weight: 600;
          cursor: pointer;
          border: none;
          transition: all 0.2s;
        }
        .btn-diff-mode {
          background: #2d3748;
          color: #a0aec0;
          border: 1px solid #4a5568;
        }
        .btn-diff-mode.active { background: #4a5568; color: #fff; }
        .btn-compare { background: #6366f1; color: #fff; }
        .btn-compare:hover { background: #4f46e5; }

        .error-banner {
          background: #742a2a;
          border: 1px solid #fc8181;
          color: #fed7d7;
          padding: 0.75rem 1rem;
          border-radius: 8px;
          margin-bottom: 1rem;
        }

        /* Diff panel */
        .diff-panel {
          background: #1a202c;
          border: 1px solid #2d3748;
          border-radius: 12px;
          padding: 1.25rem;
          margin-bottom: 1.5rem;
        }
        .diff-viewer { overflow: auto; }
        .diff-header {
          display: flex;
          gap: 0.75rem;
          align-items: center;
          font-size: 0.8rem;
          color: #718096;
          margin-bottom: 0.75rem;
        }
        .diff-from { color: #fc8181; font-weight: 600; }
        .diff-to   { color: #68d391; font-weight: 600; }
        .diff-arrow { color: #4a5568; }
        .diff-pre {
          white-space: pre-wrap;
          font-family: 'Fira Code', monospace;
          font-size: 0.78rem;
          line-height: 1.6;
          background: #0d1117;
          border-radius: 8px;
          padding: 1rem;
          max-height: 400px;
          overflow-y: auto;
          margin: 0;
        }
        .diff-insert { background: rgba(72,187,120,0.2); color: #68d391; }
        .diff-delete { background: rgba(245,101,101,0.2); color: #fc8181; text-decoration: line-through; }
        .diff-equal  { color: #a0aec0; }
        .btn-close-diff {
          margin-top: 0.75rem;
          background: transparent;
          border: 1px solid #4a5568;
          color: #718096;
          padding: 0.3rem 0.8rem;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.8rem;
        }

        /* Timeline */
        .version-timeline {
          display: flex;
          flex-direction: column;
          gap: 0;
          position: relative;
        }
        .version-timeline::before {
          content: '';
          position: absolute;
          left: 18px;
          top: 0;
          bottom: 0;
          width: 2px;
          background: linear-gradient(to bottom, #6366f1, #1a202c);
        }
        .version-card {
          background: #1a202c;
          border: 1px solid #2d3748;
          border-radius: 12px;
          padding: 1.1rem 1.25rem 1.1rem 1.5rem;
          margin-left: 36px;
          margin-bottom: 1rem;
          position: relative;
          transition: border-color 0.2s, transform 0.15s;
          cursor: default;
        }
        .version-card::before {
          content: '●';
          position: absolute;
          left: -28px;
          top: 50%;
          transform: translateY(-50%);
          color: #6366f1;
          font-size: 0.9rem;
        }
        .version-card.frozen { border-color: #4a5568; opacity: 0.85; }
        .version-card.selected {
          border-color: #6366f1;
          background: #1e1b4b;
          cursor: pointer;
          transform: translateX(4px);
        }
        .version-card:not(.frozen):hover { border-color: #4a5568; }

        .version-badge-row {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.4rem;
        }
        .version-number {
          font-weight: 700;
          font-size: 0.95rem;
          color: #a78bfa;
        }
        .frozen-badge {
          background: #2d3748;
          color: #718096;
          font-size: 0.7rem;
          border-radius: 4px;
          padding: 0.1rem 0.4rem;
        }
        .latest-badge {
          background: #276749;
          color: #68d391;
          font-size: 0.7rem;
          border-radius: 4px;
          padding: 0.1rem 0.4rem;
          font-weight: 700;
        }
        .version-comment {
          color: #e2e8f0;
          font-size: 0.9rem;
          margin: 0 0 0.5rem 0;
        }
        .version-meta {
          font-size: 0.75rem;
          color: #718096;
          margin-bottom: 0.6rem;
        }
        .version-actions {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }
        .btn-action {
          background: #2d3748;
          border: 1px solid #4a5568;
          color: #a0aec0;
          padding: 0.3rem 0.7rem;
          border-radius: 6px;
          font-size: 0.78rem;
          cursor: pointer;
          text-decoration: none;
          transition: all 0.15s;
        }
        .btn-action:hover { background: #4a5568; color: #fff; }
        .btn-action:disabled { opacity: 0.4; cursor: not-allowed; }
        .btn-action.btn-freeze { border-color: #553c9a; color: #b794f4; }
        .btn-action.btn-freeze:hover { background: #44337a; }
        .btn-action.btn-delete { border-color: #742a2a; color: #fc8181; }
        .btn-action.btn-delete:hover { background: #742a2a; color: #fff; }

        .version-checkbox {
          position: absolute;
          top: 1rem;
          right: 1rem;
          width: 18px;
          height: 18px;
          accent-color: #6366f1;
          cursor: pointer;
        }

        .loading-spinner, .empty-state {
          text-align: center;
          padding: 3rem;
          color: #718096;
          font-size: 0.9rem;
        }
      `}</style>
        </div>
    );
};

export default DocumentHistory;
